import { useRef, useState, useEffect, useContext, useMemo } from "react";

//Components
import { TextField, Box, Button, FormControl } from "@material-ui/core";
import SingleDropDown from "./SingleDropDown";
import MultiDropDownObjects from "./MultiDropDownObjects";
import SuspectVictimAffiliation from "./SuspectVictimAffiliation";
import VirtualizedAutocomplete from "./VirtualizedAutocomplete";

//Contexts
import { DashboardContext } from "../Contexts/DashboardContext";
import { SetupContext } from "../Contexts/SetupContext";
import { INCIDENT_ACTIONS } from "../Actions/IncidentActions";
import { IncidentsContext } from "../Contexts/IncidentsContext";
import { MutationContext } from "../Contexts/MutationContext";
import { UserContext } from "../Contexts/UserContext";

//Fetching
import { useQuery } from "@apollo/client";
import { GET_ORIS_QUERY } from "../Queries/GetOrisQuery";

//Styling
import { useStyles } from "../Styles/styles";

//Utils
import PropTypes from "prop-types";
import { localGetData, parseObjectArrayIdString } from "../utils/utils";
import {
  createEmptyIncidentOffense,
  createEmptySuspectVictimAffiliation,
} from "../utils/EmptyObjects";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { shouldBeLocked } from "../utils/Roles";
import { getValueFromEventValue } from "../utils/getValueFromEventValue";
import { getPoldTrigger } from "../utils/getPoldTrigger";

const Offense = ({
  offense,
  offenseIndex,
  incidentId,
  incidentIndex,
  piss,
  offenseLength,
  offenseTab,
  setOffenseTab,
  incidentOriId,
  userRoles,
  disabled = undefined,
  people,
  setPopper,
  ...props
}) => {
  const memoizedLocked = useMemo(
    () => shouldBeLocked(disabled, props?.canEdit, userRoles),
    [disabled, props?.canEdit, userRoles],
  );
  const classes = useStyles();
  const inputLabel = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);
  const [offenseViolations, setOffenseViolations] = useState([]);
  const { indexedDb } = useContext(DashboardContext);
  const { setup } = useContext(SetupContext);
  const { incidentDispatch, incidents } = useContext(IncidentsContext);
  const { ADD_OFFENSE, REMOVE_OFFENSE, UPDATE_OFFENSE, ADD_SVA, REMOVE_SVA } =
    INCIDENT_ACTIONS;
  const {
    updateOffenseInIncident,
    throttledUpdateOffenseInIncident,
    removeOffenseFromIncident,
    throttledAddSvaInOffense,
    removeSvaFromOffense,
  } = useContext(MutationContext);
  const { user } = useContext(UserContext);
  const incident = useMemo(() => {
    return (
      incidents.find(i => i.id === incidentId || i.uuid === incidentId) ||
      incidents?.[incidentIndex] ||
      null
    );
  }, [incidents, incidentId, incidentIndex]);
  const poldOptOut = useMemo(() => {
    return incident?.ori?.poldOptOut || false;
  }, [incident]);

  useEffect(() => {
    indexedDb.offense_violations
      .toArray()
      .then(res => {
        setOffenseViolations(res);
      })
      .catch(err => console.error(err));
  }, [indexedDb.offense_violations]);

  const {
    loading: orisLoading,
    error: orisError,
    data: orisData,
  } = useQuery(GET_ORIS_QUERY, {
    context: { serializationKey: "incidentQueue" },
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: () => {
      return;
    },
  });

  useEffect(() => {
    if (inputLabel.current) {
      setLabelWidth(inputLabel.current.offsetWidth);
    }
  }, [inputLabel]);

  const {
    setupLists: {
      nibrsViolationTypes,
      committedTypes,
      locationTypes,
      criminalActivityTypes: criminalActivityTypess,
      gangAffiliateds,
      assaultTypes: assaultTypess,
      weaponTypes,
      offenseIbrDesignations,
      forceNoForceTypes,
      campusCodes,
      justifiableHomicideTypes,
    },
  } = setup;

  const localStoreSpecificSetup =
    localGetData(`specific-setup-${incidentOriId}`) || {};
  const { setupLists: specificSetupLists } = localStoreSpecificSetup || {};
  const { egressTypes, significantEventTypes } = specificSetupLists || {};

  const updateAutoSuggest = (value, name) => {
    incidentDispatch({
      type: UPDATE_OFFENSE,
      payload: {
        incidentId: incidentId,
        offenseId: offense.id,
        uuid: offense.uuid,
        property: name,
        value: value,
      },
    });
    const fieldToUpdate = stateToMutation[name];
    value?.id &&
      updateOffenseInIncident({
        variables: {
          input: {
            incidentId: incidentId,
            originatorId: user?.id,
            incidentOffenseId: offense.id,
            [fieldToUpdate]: value.id,
          },
        },
        context: { serializationKey: "incidentQueue" },
      });
  };

  const [incidentPeople, setIncidentPeople] = useState();

  useEffect(() => {
    let persons = [];
    piss.forEach(pis => {
      let name = `${pis?.person?.personalAttribute?.lastName}, ${pis?.person?.personalAttribute?.firstName}`;
      let person = { ...pis?.person, name: name };
      persons.push(person);
      return null;
    });
    setIncidentPeople(persons);
  }, [piss]);

  const stateToMutation = {
    campusCode: "campusCodeId",
    entryPointType: "entryPointTypeId",
    exitPointType: "exitPointTypeId",
    forceNoForceType: "forceNoForceTypeId",
    justifiableHomicideType: "justifiableHomicideTypeId",
    //Not sure where this is used
    // "larcenyCode": "larcenyCodeId",
    locationType: "locationTypeId",
    nibrsViolationType: "nibrsViolationTypeId",
    offenseViolationPrototype: "offenseViolationPrototypeId",
    //Weird typing issue
    numberOfPremisesEntered: "numberOfPremisesEntered",
    offenseIbrDesignation: "offenseIbrDesignationId",
    significantEvent: "significantEventId",
    //Not sure how this is used. It may just be for printing
    // "sealed": "sealed",
    counts: "counts",
    //Not sure where this is used (and yes there's no id - it's a string)
    // "ibrCode": "ibrCode",
    committedType: "committedTypeId",
    offenseWeaponTypes: "offenseWeaponTypeIds",
    assaultTypes: "assaultTypeIds",
    offenseGangAffiliateds: "offenseGangAffiliatedIds",
    criminalActivityTypes: "criminalActivityTypeIds",
  };

  const handleChange = (event, submit = true) => {
    const name = event.target.name || null;
    const value = event.target.value;
    const mutationValue = getValueFromEventValue(event.target.value);
    const fieldToUpdate = stateToMutation[name];
    if (!name) {
      alert(
        `Please add a name field that matches the state name to the component that was just updated`,
      );
      return null;
    } else {
      switch (name) {
        case "nibrsViolationType":
          incidentDispatch({
            type: UPDATE_OFFENSE,
            payload: {
              incidentId: incidentId,
              offenseId: offense.id,
              uuid: offense.uuid,
              property: name,
              value: value,
            },
          });
          submit &&
            updateOffenseInIncident({
              variables: {
                input: {
                  incidentId: incidentId,
                  originatorId: user?.id,
                  incidentOffenseId: offense.id,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;

        case "counts":
          incidentDispatch({
            type: UPDATE_OFFENSE,
            payload: {
              incidentId: incidentId,
              offenseId: offense.id,
              uuid: offense.uuid,
              property: name,
              value: value,
            },
          });
          submit &&
            updateOffenseInIncident({
              variables: {
                input: {
                  incidentId: incidentId,
                  originatorId: user?.id,
                  incidentOffenseId: offense.id,
                  [fieldToUpdate]: +value,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;

        case "numberOfPremisesEntered":
          incidentDispatch({
            type: UPDATE_OFFENSE,
            payload: {
              incidentId: incidentId,
              offenseId: offense.id,
              uuid: offense.uuid,
              property: name,
              value: value,
            },
          });
          //There's a type issue here
          submit &&
            updateOffenseInIncident({
              variables: {
                input: {
                  incidentId: incidentId,
                  originatorId: user?.id,
                  incidentOffenseId: offense.id,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;

        case "locationType":
          incidentDispatch({
            type: UPDATE_OFFENSE,
            payload: {
              incidentId: incidentId,
              offenseId: offense.id,
              uuid: offense.uuid,
              property: name,
              value: value,
            },
          });
          if (value?.name?.startsWith("Bar/Night")) {
            const { needsPoldWarning } = getPoldTrigger({
              incident,
              poldOptOut,
              forceBarNight: true,
            });
            if (needsPoldWarning && !!setPopper) {
              setPopper({
                open: true,
                severity: "info",
                content:
                  "Please fill out the POLD field in Initial Call Information.",
              });
            }
          }
          submit &&
            updateOffenseInIncident({
              variables: {
                input: {
                  incidentId: incidentId,
                  originatorId: user?.id,
                  incidentOffenseId: offense.id,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;

        case "committedType":
          incidentDispatch({
            type: UPDATE_OFFENSE,
            payload: {
              incidentId: incidentId,
              offenseId: offense.id,
              uuid: offense.uuid,
              property: name,
              value: value,
            },
          });
          submit &&
            updateOffenseInIncident({
              variables: {
                input: {
                  incidentId: incidentId,
                  originatorId: user?.id,
                  incidentOffenseId: offense.id,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;

        case "criminalActivityTypes":
          //multi select
          incidentDispatch({
            type: UPDATE_OFFENSE,
            payload: {
              incidentId: incidentId,
              offenseId: offense.id,
              uuid: offense.uuid,
              property: name,
              value: value,
            },
          });
          submit &&
            updateOffenseInIncident({
              variables: {
                input: {
                  incidentId: incidentId,
                  originatorId: user?.id,
                  incidentOffenseId: offense.id,
                  [fieldToUpdate]: parseObjectArrayIdString(value),
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;

        case "offenseGangAffiliateds":
          //multi select
          incidentDispatch({
            type: UPDATE_OFFENSE,
            payload: {
              incidentId: incidentId,
              offenseId: offense.id,
              uuid: offense.uuid,
              property: name,
              value: value,
            },
          });
          submit &&
            updateOffenseInIncident({
              variables: {
                input: {
                  incidentId: incidentId,
                  originatorId: user?.id,
                  incidentOffenseId: offense.id,
                  [fieldToUpdate]: parseObjectArrayIdString(value),
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;

        case "assaultTypes":
          //multi select
          incidentDispatch({
            type: UPDATE_OFFENSE,
            payload: {
              incidentId: incidentId,
              offenseId: offense.id,
              uuid: offense.uuid,
              property: name,
              value: value,
            },
          });
          submit &&
            updateOffenseInIncident({
              variables: {
                input: {
                  incidentId: incidentId,
                  originatorId: user?.id,
                  incidentOffenseId: offense.id,
                  [fieldToUpdate]: parseObjectArrayIdString(value),
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;

        case "offenseWeaponTypes":
          //multi select
          incidentDispatch({
            type: UPDATE_OFFENSE,
            payload: {
              incidentId: incidentId,
              offenseId: offense.id,
              uuid: offense.uuid,
              property: name,
              value: value,
            },
          });
          submit &&
            updateOffenseInIncident({
              variables: {
                input: {
                  incidentId: incidentId,
                  originatorId: user?.id,
                  incidentOffenseId: offense.id,
                  [fieldToUpdate]: parseObjectArrayIdString(value),
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;

        case "offenseIbrDesignation":
          incidentDispatch({
            type: UPDATE_OFFENSE,
            payload: {
              incidentId: incidentId,
              offenseId: offense.id,
              uuid: offense.uuid,
              property: name,
              value: value,
            },
          });
          submit &&
            updateOffenseInIncident({
              variables: {
                input: {
                  incidentId: incidentId,
                  originatorId: user?.id,
                  incidentOffenseId: offense.id,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;

        case "entryPointType":
          incidentDispatch({
            type: UPDATE_OFFENSE,
            payload: {
              incidentId: incidentId,
              offenseId: offense.id,
              uuid: offense.uuid,
              property: name,
              value: value,
            },
          });
          submit &&
            updateOffenseInIncident({
              variables: {
                input: {
                  incidentId: incidentId,
                  originatorId: user?.id,
                  incidentOffenseId: offense.id,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;

        case "forceNoForceType":
          incidentDispatch({
            type: UPDATE_OFFENSE,
            payload: {
              incidentId: incidentId,
              offenseId: offense.id,
              uuid: offense.uuid,
              property: name,
              value: value,
            },
          });
          submit &&
            updateOffenseInIncident({
              variables: {
                input: {
                  incidentId: incidentId,
                  originatorId: user?.id,
                  incidentOffenseId: offense.id,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;

        case "exitPointType":
          incidentDispatch({
            type: UPDATE_OFFENSE,
            payload: {
              incidentId: incidentId,
              offenseId: offense.id,
              uuid: offense.uuid,
              property: name,
              value: value,
            },
          });
          submit &&
            updateOffenseInIncident({
              variables: {
                input: {
                  incidentId: incidentId,
                  originatorId: user?.id,
                  incidentOffenseId: offense.id,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;

        case "campusCode":
          incidentDispatch({
            type: UPDATE_OFFENSE,
            payload: {
              incidentId: incidentId,
              offenseId: offense.id,
              uuid: offense.uuid,
              property: name,
              value: value,
            },
          });
          submit &&
            updateOffenseInIncident({
              variables: {
                input: {
                  incidentId: incidentId,
                  originatorId: user?.id,
                  incidentOffenseId: offense.id,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;

        case "justifiableHomicideType":
          incidentDispatch({
            type: UPDATE_OFFENSE,
            payload: {
              incidentId: incidentId,
              offenseId: offense.id,
              uuid: offense.uuid,
              property: name,
              value: value,
            },
          });
          submit &&
            updateOffenseInIncident({
              variables: {
                input: {
                  incidentId: incidentId,
                  originatorId: user?.id,
                  incidentOffenseId: offense.id,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;

        case "significantEvent":
          incidentDispatch({
            type: UPDATE_OFFENSE,
            payload: {
              incidentId: incidentId,
              offenseId: offense.id,
              uuid: offense.uuid,
              property: name,
              value: value,
            },
          });
          submit &&
            updateOffenseInIncident({
              variables: {
                input: {
                  incidentId: incidentId,
                  originatorId: user?.id,
                  incidentOffenseId: offense.id,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;

        default:
          alert("Name not found in handleChange function in Offense.js");
          return null;
      }
    }
  };

  const handleAutoSuggestChange = (value, name) => {
    const fieldToUpdate = stateToMutation[name];
    switch (name) {
      case "offenseWeaponTypes":
        incidentDispatch({
          type: UPDATE_OFFENSE,
          payload: {
            incidentId: incidentId,
            offenseId: offense.id,
            uuid: offense.uuid,
            property: name,
            value: value,
          },
        });
        updateOffenseInIncident({
          variables: {
            input: {
              incidentId: incidentId,
              originatorId: user?.id,
              incidentOffenseId: offense.id,
              [fieldToUpdate]: parseObjectArrayIdString(value),
            },
          },
          context: { serializationKey: "incidentQueue" },
        });
        return null;
      default:
        alert("Name not found in handleChange function in Offense.js");
        return null;
    }
  };

  const addOffenseVictimSuspect = () => {
    let emptySva = createEmptySuspectVictimAffiliation();
    incidentDispatch({
      type: ADD_SVA,
      payload: {
        incidentId: incidentId,
        offenseId: offense.id,
        sva: emptySva,
      },
    });
    throttledAddSvaInOffense({
      variables: {
        input: {
          originatorId: user?.id,
          incidentOffenseId: offense.id,
          suspectVictimAffiliationId: emptySva.uuid,
        },
      },
      context: { serializationKey: "incidentQueue" },
    });
  };

  const removeOffenseVictimSuspect = (svaId) => {
    incidentDispatch({
      type: REMOVE_SVA,
      payload: {
        incidentId: incidentId,
        offenseId: offense.id,
        svaId: svaId,
      },
    });
    removeSvaFromOffense({
      variables: {
        input: { originatorId: user?.id, suspectVictimAffiliationId: svaId },
      },
      context: { serializationKey: "incidentQueue" },
    });
  };

  const handleRemoveOffense = offenseId => {
    if (
      window.confirm(`Are you sure you want to delete Offense ${offense.id}?`)
    ) {
      incidentDispatch({
        type: REMOVE_OFFENSE,
        payload: {
          incidentId: incidentId,
          offenseId: offense.id,
          uuid: offense.uuid,
        },
      });
      removeOffenseFromIncident({
        variables: {
          input: { originatorId: user?.id, incidentOffenseId: offenseId },
        },
        context: { serializationKey: "incidentQueue" },
      });
      if (offenseLength <= 1) {
        handleAddOffense();
      }
      setOffenseTab(offenseTab > 0 ? offenseTab - 1 : 0);
      return null;
    }
  };

  const handleAddOffense = () => {
    const emptyOffense = createEmptyIncidentOffense();
    incidentDispatch({
      type: ADD_OFFENSE,
      payload: {
        incidentId: incidentId,
        offense: emptyOffense,
      },
    });
    setOffenseTab(offenseLength);
    throttledUpdateOffenseInIncident({
      variables: {
        input: {
          incidentId: incidentId,
          originatorId: user?.id,
          incidentOffenseId: emptyOffense.uuid,
        },
      },
      context: { serializationKey: "incidentQueue" },
    });
    throttledAddSvaInOffense({
      variables: {
        input: {
          originatorId: user?.id,
          incidentOffenseId: emptyOffense.uuid,
          suspectVictimAffiliationId:
            emptyOffense.suspectVictimAffiliations[0].uuid,
        },
      },
      context: { serializationKey: "incidentQueue" },
    });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        maxWidth: "100%",
        borderBottom: "1px solid black",
        justifyContent: "flex-start",
      }}
    >
      <VirtualizedAutocomplete
        title={"Violation"}
        loading={false}
        options={offenseViolations}
        error={null}
        updateFields={updateAutoSuggest}
        value={offense.offenseViolationPrototype}
        stateName={"offenseViolationPrototype"}
        oriId={incidentOriId}
        userRoles={userRoles}
        {...props}
      />
      <TextField
        className={classes.formControl}
        data-cy="offenseCategory"
        inputProps={{ "data-cy": "offenseCategory-input" }}
        InputLabelProps={{ shrink: true }}
        id="offenseCategory-id"
        label="Category"
        value={offense?.offenseViolationPrototype?.offenseCategory?.name || ""}
        disabled={true}
      />
      <TextField
        className={classes.formControl}
        data-cy="offenseSubCategory"
        inputProps={{ "data-cy": "offenseSubCategory-input" }}
        InputLabelProps={{ shrink: true }}
        id="offenseSubCategory-id"
        label="Subcategory"
        value={
          offense?.offenseViolationPrototype?.offenseSubCategory?.name || ""
        }
        disabled={true}
      />
      <SingleDropDown
        handleChange={handleChange}
        stateName={"nibrsViolationType"}
        value={offense?.nibrsViolationType}
        options={nibrsViolationTypes}
        title={"NIBRS Vio Type"}
        classStyle={classes.formControl}
        inputLabel={inputLabel}
        labelWidth={labelWidth}
        userRoles={userRoles}
        {...props}
      />
      <TextField
        className={classes.formControl}
        data-cy="counts"
        inputProps={{ "data-cy": "counts-input" }}
        InputLabelProps={{ shrink: true }}
        id="counts-id"
        disabled={memoizedLocked}
        label="Counts"
        value={offense?.counts || ""}
        name={"counts"}
        onChange={e => handleChange(e, false)}
        onBlur={e => handleChange(e, true)}
        onKeyDown={e => {
          if (e.key === "Enter") {
            e.target.blur();
          }
        }}
      />
      <TextField
        className={classes.formControl}
        data-cy="numberOfPremisesEntered"
        inputProps={{ "data-cy": "numberOfPremisesEntered-input" }}
        InputLabelProps={{ shrink: true }}
        disabled={memoizedLocked}
        id="numberOfPremisesEntered-id"
        label="Premises"
        value={offense?.numberOfPremisesEntered || ""}
        name={"numberOfPremisesEntered"}
        onChange={e => handleChange(e, false)}
        onBlur={e => handleChange(e, true)}
        onKeyDown={e => {
          if (e.key === "Enter") {
            e.target.blur();
          }
        }}
      />
      {/* Needs to be updated to be only a single ID */}
      <SingleDropDown
        handleChange={handleChange}
        stateName={"committedType"}
        value={offense?.committedType}
        options={committedTypes}
        title={"Comm/Att"}
        classStyle={classes.formControl}
        inputLabel={inputLabel}
        labelWidth={labelWidth}
        userRoles={userRoles}
        {...props}
      />
      <SingleDropDown
        handleChange={handleChange}
        stateName={"locationType"}
        value={offense?.locationType}
        options={locationTypes}
        title={"IBR Scene/Loc Type"}
        classStyle={classes.formControl}
        inputLabel={inputLabel}
        labelWidth={labelWidth}
        userRoles={userRoles}
        {...props}
      />
      <MultiDropDownObjects
        formStyle={classes.formControl}
        labelStyle={classes.multiInputLabel}
        selectStyle={null}
        chipsStyle={classes.chips}
        chipStyle={classes.chip}
        id={"criminalActivityTypes"}
        title={"IBR Crim Act Type"}
        stateName={"criminalActivityTypes"}
        handleChange={handleChange}
        selectedValues={
          offense?.criminalActivityTypes
            ? criminalActivityTypess.filter(option =>
                offense?.criminalActivityTypes.some(
                  types => option.id === types.id,
                ),
              )
            : []
        }
        options={criminalActivityTypess}
        userRoles={userRoles}
        {...props}
      />
      <MultiDropDownObjects
        formStyle={classes.formControl}
        labelStyle={classes.multiInputLabel}
        selectStyle={null}
        chipsStyle={classes.chips}
        chipStyle={classes.chip}
        id={"offenseGangAffiliateds"}
        title={"IBR Gang Affil"}
        stateName={"offenseGangAffiliateds"}
        handleChange={handleChange}
        selectedValues={
          offense?.offenseGangAffiliateds
            ? gangAffiliateds.filter(option =>
                offense?.offenseGangAffiliateds.some(
                  types => option.id === types.id,
                ),
              )
            : []
        }
        options={gangAffiliateds}
        userRoles={userRoles}
        {...props}
      />

      <MultiDropDownObjects
        formStyle={classes.formControl}
        labelStyle={classes.multiInputLabel}
        selectStyle={null}
        chipsStyle={classes.chips}
        chipStyle={classes.chip}
        id={"assaultTypes"}
        title={"IBR Agg.Aslt/Hom."}
        stateName={"assaultTypes"}
        handleChange={handleChange}
        selectedValues={
          offense?.assaultTypes
            ? assaultTypess.filter(option =>
                offense?.assaultTypes.some(types => option.id === types.id),
              )
            : []
        }
        options={assaultTypess}
        userRoles={userRoles}
        {...props}
      />
      <FormControl className={classes.formControl}>
        <Autocomplete
          multiple
          id="weapon-type-autocomplete"
          size="small"
          disabled={memoizedLocked}
          options={weaponTypes}
          getOptionLabel={option => option.name}
          value={
            offense?.offenseWeaponTypes
              ? weaponTypes.filter(option =>
                  offense?.offenseWeaponTypes.some(
                    types => option.id === types.id,
                  ),
                )
              : []
          }
          onChange={(e, value) =>
            handleAutoSuggestChange(value, "offenseWeaponTypes")
          }
          renderInput={params => (
            <TextField
              {...params}
              InputLabelProps={{ shrink: true }}
              variant="standard"
              label="IBR&nbsp;Weapon&nbsp;Type"
            />
          )}
        />
      </FormControl>
      <SingleDropDown
        handleChange={handleChange}
        stateName={"offenseIbrDesignation"}
        value={offense?.offenseIbrDesignation}
        options={offenseIbrDesignations}
        title={"NIBRS Override"}
        classStyle={classes.formControl}
        inputLabel={inputLabel}
        labelWidth={labelWidth}
        userRoles={userRoles}
        {...props}
      />
      <SingleDropDown
        handleChange={handleChange}
        stateName={"entryPointType"}
        value={offense?.entryPointType}
        options={egressTypes}
        title={"Point of Entry"}
        classStyle={classes.formControl}
        inputLabel={inputLabel}
        labelWidth={labelWidth}
        userRoles={userRoles}
        {...props}
      />
      <SingleDropDown
        handleChange={handleChange}
        stateName={"forceNoForceType"}
        value={offense?.forceNoForceType}
        options={forceNoForceTypes}
        title={"Force/No Force"}
        classStyle={classes.formControl}
        inputLabel={inputLabel}
        labelWidth={labelWidth}
        userRoles={userRoles}
        {...props}
      />
      <SingleDropDown
        handleChange={handleChange}
        stateName={"exitPointType"}
        value={offense?.exitPointType}
        options={egressTypes}
        title={"Point of Exit"}
        classStyle={classes.formControl}
        inputLabel={inputLabel}
        labelWidth={labelWidth}
        userRoles={userRoles}
        {...props}
      />
      <SingleDropDown
        handleChange={handleChange}
        stateName={"campusCode"}
        value={offense?.campusCode}
        options={campusCodes}
        title={"Campus Code"}
        classStyle={classes.formControl}
        inputLabel={inputLabel}
        labelWidth={labelWidth}
        userRoles={userRoles}
        {...props}
      />
      <SingleDropDown
        handleChange={handleChange}
        stateName={"justifiableHomicideType"}
        value={offense?.justifiableHomicideType}
        options={justifiableHomicideTypes}
        title={"Justifiable Homicide"}
        classStyle={classes.formControl}
        inputLabel={inputLabel}
        labelWidth={labelWidth}
        userRoles={userRoles}
        {...props}
      />
      <SingleDropDown
        handleChange={handleChange}
        stateName={"significantEvent"}
        value={offense?.significantEvent}
        options={significantEventTypes}
        title={"Significant Event"}
        classStyle={classes.formControl}
        inputLabel={inputLabel}
        labelWidth={labelWidth}
        userRoles={userRoles}
        {...props}
      />
      <div
        style={{ width: "100%", margin: "0 16px" }}
        id="responding-officer-container"
        data-cy="responding-officer-container"
      >
        {offense?.suspectVictimAffiliations?.map((sva, index) => {
          return (
            <SuspectVictimAffiliation
              formStyle={classes.formControl}
              fabStyle={classes.fabButton}
              addOffenseVictimSuspect={addOffenseVictimSuspect}
              removeOffenseVictimSuspect={removeOffenseVictimSuspect}
              svaLength={offense?.suspectVictimAffiliations.length}
              incidentPeople={incidentPeople}
              ovsIndex={index}
              key={sva.id}
              sva={sva}
              svaIndex={index}
              orisData={orisData}
              orisLoading={orisLoading}
              orisError={orisError}
              incidentId={incidentId}
              incidentIndex={incidentIndex}
              offenseId={offense.id}
              offenseIndex={offenseIndex}
              userRoles={userRoles}
              {...props}
            />
          );
        })}
      </div>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-around"
        style={{ width: "100%" }}
      >
        <Button
          style={{
            width: "fit-content",
            marginTop: "3vh",
            marginBottom: "3vh",
            marginRight: "2vw",
          }}
          disabled={memoizedLocked}
          variant="contained"
          onClick={() => handleRemoveOffense(offense.id || offense.uuid)}
        >
          Delete Offense
        </Button>
        <Button
          style={{
            width: "fit-content",
            marginTop: "3vh",
            marginBottom: "3vh",
            marginRight: "2vw",
          }}
          disabled={memoizedLocked}
          variant="contained"
          onClick={handleAddOffense}
        >
          Add Offense
        </Button>
        {/* <Button style={{ width: "fit-content", marginTop: '3vh', marginBottom: '3vh', marginRight: "2vw" }} variant="contained" onClick={() => alert('Add printing')}>Print</Button> */}
      </Box>
    </div>
  );
};

Offense.propTypes = {
  setup: PropTypes.object,
  people: PropTypes.array,
  incidentId: PropTypes.string.isRequired,
  incidentIndex: PropTypes.number.isRequired,
  offense: PropTypes.object.isRequired,
  offenseIndex: PropTypes.number.isRequired,
  offenseLength: PropTypes.number.isRequired,
  incidentOriId: PropTypes.string.isRequired,
  offenseTab: PropTypes.number.isRequired,
  setOffenseTab: PropTypes.func.isRequired,
  piss: PropTypes.array.isRequired,
  userRoles: PropTypes.array.isRequired,
  user: PropTypes.object,
  setPopper: PropTypes.func,
};

export default Offense;
